import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import type * as API from "@shared-core/data/api-types";
import { Card } from "../../primitives/card";
import { Input } from "../../primitives/input";
import { ListPagination } from "../../patterns/ListPagination";

// Insights -> Alert Feedback: every rep-authored reason behind a "no need to
// watch" alert mute or a health-tier override, newest first. The
// self-improvement loop product asked for — a manager reads these in
// aggregate, spots recurring reasons, and feeds them back into the scoring/
// alert rules. Named-individual, customer-linked activity, so the tab is
// gated the same as Product Usage (admin/manager only — see the backend
// route's docstring).

type FeedbackType = API.AlertFeedbackEntry["type"];
const PAGE_SIZE = 25;

const TYPE_FILTERS: { id: FeedbackType | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "customer_alert_suppression", label: "Alert mutes" },
  { id: "customer_health_tier_override", label: "Health overrides" },
];

function TypeBadge({ type }: { type: FeedbackType }) {
  return type === "customer_alert_suppression" ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
      Alert mute
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
      Health override
    </span>
  );
}

function WhatChanged({ row }: { row: API.AlertFeedbackEntry }) {
  return row.type === "customer_alert_suppression" ? (
    <span>{row.alert_name || "Alert"}</span>
  ) : (
    <span className="capitalize">{row.override_tier || "—"}</span>
  );
}

type State =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      items: API.AlertFeedbackEntry[];
      total: number;
      totalPages: number;
    };

export function AlertFeedbackTab() {
  const adapter = useDataAdapter();
  const [typeFilter, setTypeFilter] = useState<FeedbackType | "all">("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    setPage(1);
  }, [typeFilter, debouncedSearch]);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: "loading" });
    adapter
      .getAlertFeedback({
        type: typeFilter === "all" ? undefined : [typeFilter],
        q: debouncedSearch.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
        sort: "-set_at",
      })
      .then((result) => {
        if (cancelled) return;
        setState({
          status: "ready",
          items: result.items,
          total: result.total,
          totalPages: result.totalPages,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, typeFilter, debouncedSearch, page]);

  useEffect(() => load(), [load]);

  const items = state.status === "ready" ? state.items : [];
  const total = state.status === "ready" ? state.total : 0;
  const totalPages = state.status === "ready" ? state.totalPages : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg">Alert feedback</h2>
          <p className="text-sm text-muted-foreground">
            {total} reason{total === 1 ? "" : "s"} reps have given for muting an
            alert or overriding a health score — read these for recurring
            patterns worth feeding back into the scoring rules
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1.5">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setTypeFilter(f.id)}
                className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                  typeFilter === f.id
                    ? "bg-primary text-white"
                    : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer or reason..."
            aria-label="Search customer or reason"
            className="h-7 text-xs w-[220px]"
          />
        </div>
      </div>

      <Card className="shadow-md border-gray-300 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-normal">Set</th>
                <th className="px-3 py-2 font-normal">Customer</th>
                <th className="px-3 py-2 font-normal">Type</th>
                <th className="px-3 py-2 font-normal">Alert / tier</th>
                <th className="px-3 py-2 font-normal">Reason</th>
                <th className="px-3 py-2 font-normal">Set by</th>
              </tr>
            </thead>
            <tbody>
              {state.status === "loading" && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {state.status === "error" && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center">
                    <p className="text-destructive text-sm mb-2">
                      Couldn’t load alert feedback. Please try again.
                    </p>
                    <button
                      type="button"
                      onClick={load}
                      className="text-primary text-sm underline hover:no-underline"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={`${row.type}_${row.id}`}
                  className="border-b border-border last:border-0 align-top"
                >
                  <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
                    {formatDateTimeOrDash(row.set_at)}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <Link
                      to={paths.customerDetail({ customerId: row.customer_id })}
                      className="underline hover:text-foreground"
                    >
                      {row.customer_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <TypeBadge type={row.type} />
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <WhatChanged row={row} />
                  </td>
                  <td className="px-3 py-2.5 max-w-[420px]">{row.reason}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                    {row.set_by_user_name || "—"}
                  </td>
                </tr>
              ))}
              {state.status === "ready" && items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    {debouncedSearch.trim() || typeFilter !== "all"
                      ? "No feedback matches your filters"
                      : "No alert mutes or health overrides recorded yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
